const PREVIEW_FORMAT_LABELS = {
  svg: "SVG",
  webp: "WebP",
  png: "PNG",
  jpg: "JPEG",
  jpeg: "JPEG",
  gif: "GIF",
};

export function getPreviewFormatFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  const path = url.split("?")[0].split("#")[0].toLowerCase();
  const match = path.match(/\.([a-z0-9]+)$/);
  if (!match) return null;
  const ext = match[1];
  return PREVIEW_FORMAT_LABELS[ext] ? ext : null;
}

export function getPreviewFormatLabel(url) {
  const ext = getPreviewFormatFromUrl(url);
  return ext ? PREVIEW_FORMAT_LABELS[ext] : null;
}

export function isBrowserPreviewableUrl(url) {
  const ext = getPreviewFormatFromUrl(url);
  if (!ext) return Boolean(url);
  return ["svg", "webp", "png", "jpg", "jpeg", "gif"].includes(ext);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function normalizeComponents(schemaLike) {
  if (!schemaLike) return [];

  if (Array.isArray(schemaLike.components)) {
    return schemaLike.components;
  }

  if (typeof schemaLike === "object" && schemaLike !== null) {
    return Object.values(schemaLike).filter(Boolean);
  }

  return [];
}

export function hasFileData(defaultValue) {
  if (defaultValue === undefined || defaultValue === null) return false;
  if (Array.isArray(defaultValue)) return defaultValue.length > 0;
  if (typeof defaultValue === "string") return defaultValue.trim().length > 0;
  return true;
}

function isImageFileEntry(file, component) {
  if (!file) return false;
  const key = (component.key || "").toLowerCase();
  if (key.includes("photo") || key.includes("image") || key.includes("logo")) {
    return true;
  }
  const type = String(file.type || file.mimetype || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  const name = String(file.name || file.originalName || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp|svg)$/i.test(name);
}

function getFileEntries(defaultValue) {
  if (!hasFileData(defaultValue)) return [];
  if (Array.isArray(defaultValue)) return defaultValue;
  return [defaultValue];
}

function formatFileSize(size) {
  if (size === undefined || size === null || size === "") return "";
  const numeric = Number(size);
  if (!Number.isNaN(numeric) && numeric > 0) {
    if (numeric < 1024) return `${numeric} B`;
    if (numeric < 1024 * 1024) return `${(numeric / 1024).toFixed(1)} KB`;
    return `${(numeric / (1024 * 1024)).toFixed(1)} MB`;
  }
  return String(size);
}

function getFilePreviewSrc(file) {
  if (!file) return "";
  if (typeof file === "string") {
    if (file.startsWith("data:") || file.startsWith("http")) return file;
    return `data:image/png;base64,${file}`;
  }
  if (file.url) return file.url;
  if (file.data) {
    const data = file.data;
    if (typeof data === "string" && data.startsWith("data:")) return data;
    const mime = file.type || file.mimetype || "image/png";
    return `data:${mime};base64,${data}`;
  }
  return "";
}

function buildRequiredMark(isRequired) {
  return isRequired
    ? '<span class="text-cfStatusBlockedText ml-0.5" aria-hidden="true">*</span>'
    : "";
}

function buildEmptyFilePreviewHtml(component, t) {
  const label = escapeHtml(component.label || component.key || "File");
  const maxSize = component.fileMaxSize
    ? escapeHtml(
        t("certificateOnboarding:forms_preview_max_size", {
          size: component.fileMaxSize,
        }),
      )
    : "";
  const isRequired = component.validate?.required;

  return `
    <div class="py-3 border-b border-cfCardBorder last:border-b-0">
      <div class="text-sm font-medium text-cfSignInSignUpInputLabelText">
        ${label}${buildRequiredMark(isRequired)}
      </div>
      ${maxSize ? `<p class="mt-1 text-[0.8125rem] text-cfGlobalText">${maxSize}</p>` : ""}
    </div>
  `;
}

function buildFileWithDataPreviewHtml(component, files, t) {
  const label = escapeHtml(component.label || component.key || "File");
  const isRequired = component.validate?.required;
  const attachedLabel = escapeHtml(
    t("certificateOnboarding:forms_preview_file_attached"),
  );

  const fileRows = files
    .map((file) => {
      const name = escapeHtml(
        typeof file === "string"
          ? component.label || "File"
          : file.name || file.originalName || "File",
      );
      const size = formatFileSize(
        typeof file === "object" ? file.size : undefined,
      );
      const src = getFilePreviewSrc(file);
      const showImage = src && isImageFileEntry(file, component);

      return `
        <div class="flex items-center gap-3">
          ${
            showImage
              ? `<img src="${escapeHtml(src)}" alt="${name}" class="w-12 h-12 object-cover rounded-lg border border-cfCardBorder bg-cfSecondary" />`
              : ""
          }
          <div class="flex flex-col gap-0.5 min-w-0">
            <span class="text-sm font-medium text-cfHeadingText break-words">${name}</span>
            ${size ? `<span class="text-xs text-cfGlobalText">${escapeHtml(size)}</span>` : ""}
          </div>
        </div>
      `;
    })
    .join("");

  return `
    <div class="py-3 border-b border-cfCardBorder last:border-b-0">
      <div class="text-sm font-medium text-cfSignInSignUpInputLabelText">
        ${label}${buildRequiredMark(isRequired)}
      </div>
      <p class="mt-1 text-[0.8125rem] text-cfGlobalText">${attachedLabel}</p>
      <div class="mt-2 flex flex-col gap-2">${fileRows}</div>
    </div>
  `;
}

function transformFileComponent(component, t) {
  const files = getFileEntries(component.defaultValue);
  const content = files.length
    ? buildFileWithDataPreviewHtml(component, files, t)
    : buildEmptyFilePreviewHtml(component, t);

  return {
    type: "htmlelement",
    tag: "div",
    key: component.key,
    input: false,
    tableView: false,
    className: "mb-1",
    content,
  };
}

export function transformComponentsForPreview(components, t) {
  if (!Array.isArray(components)) return [];

  return components.map((component) => {
    if (!component || typeof component !== "object") return component;

    if (Array.isArray(component.components)) {
      return {
        ...component,
        components: transformComponentsForPreview(component.components, t),
      };
    }

    if (component.type === "file") {
      return transformFileComponent(component, t);
    }

    return component;
  });
}

export function buildPreviewSubmission(components) {
  const data = {};

  const walk = (items) => {
    if (!Array.isArray(items)) return;

    items.forEach((component) => {
      if (!component || typeof component !== "object") return;

      if (Array.isArray(component.components)) {
        walk(component.components);
        return;
      }

      if (!component.key || component.type === "htmlelement") return;

      if (component.type === "file") {
        if (hasFileData(component.defaultValue)) {
          data[component.key] = component.defaultValue;
        }
        return;
      }

      if (
        (component.type === "datetime" || component.type === "day") &&
        component.defaultDate
      ) {
        data[component.key] = component.defaultDate;
        return;
      }

      if (
        component.defaultValue !== undefined &&
        component.defaultValue !== null &&
        component.defaultValue !== ""
      ) {
        data[component.key] = component.defaultValue;
      }
    });
  };

  walk(components);
  return { data };
}

export function prepareReadOnlyFormPreview(schemaLike, t) {
  const rawComponents = normalizeComponents(schemaLike);

  if (rawComponents.length === 0) {
    return { schema: null, submission: { data: {} } };
  }

  const transformedComponents = transformComponentsForPreview(rawComponents, t);
  const submission = buildPreviewSubmission(rawComponents);

  return {
    schema: {
      display: "form",
      components: transformedComponents,
    },
    submission,
  };
}
